import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, TrendingUp, Users, Mail, Eye, Zap, UserPlus, MessageSquare, CheckCircle } from 'lucide-react';
import { getDashboardData, getDdfStatus } from '../lib/api';
import OpsLayout from '../components/OpsLayout';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const PIE_COLORS = ['#10b981', '#f59e0b', '#cbd5e1'];
const STATUS_COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#ef4444'];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [ddf, setDdf] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!localStorage.getItem('admin_token')) { navigate('/ops/login'); return; }
    loadData();
    const interval = setInterval(loadData, 60000); // auto-refresh every 60s
    return () => clearInterval(interval);
  }, [navigate]);

  const loadData = () => {
    const token = localStorage.getItem('admin_token');
    // Dashboard data is the critical call
    getDashboardData()
      .then(r => setD(r.data))
      .catch(e => { console.error('Dashboard data failed:', e); })
      .finally(() => setLoading(false));

    getDdfStatus().then(r => setDdf(r.data)).catch(() => {});

    // Activity feed is secondary — don't block dashboard on it
    axios.get(`${API_BASE}/ops/leads`, { params: { limit: 20, authorization: `Bearer ${token}` } })
      .then(r => {
          // Build activity feed from recent leads + their audit logs
          const feeds = [];
          const today = new Date().toISOString().split('T')[0];
          (r.data.leads || []).forEach(lead => {
            const name = lead.lead?.first_name || lead.lead?.email || 'Unknown';
            const created = lead.created_at || '';
            if (created.startsWith(today)) {
              feeds.push({ type: 'lead_created', name, email: lead.lead?.email, tier: lead.intent?.tier, score: lead.intent?.score, ts: created, id: lead.id });
            }
            (lead.audit_log || []).forEach(a => {
              if (a.ts && a.ts.startsWith(today)) {
                feeds.push({ type: a.action, name, field: a.field, old: a.old, new: a.new, by: a.by, ts: a.ts, id: lead.id });
              }
            });
          });
          feeds.sort((a, b) => b.ts.localeCompare(a.ts));
          setActivity(feeds.slice(0, 15));
        })
      .catch(() => {});
  };

  if (loading) return (
    <OpsLayout><div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div></OpsLayout>
  );
  if (!d) return <OpsLayout><div className="p-10 text-slate-500">Failed to load.</div></OpsLayout>;

  const convRate = d.funnel.sessions > 0 ? Math.round((d.funnel.captured / d.funnel.sessions) * 100) : 0;
  const chartData = (d.leads_by_day || []).slice(-14).map(item => ({
    ...item,
    label: new Date(item.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
  }));
  const tierData = [
    { name: 'Hot', value: d.leads.hot },
    { name: 'Warm', value: d.leads.warm },
    { name: 'Research', value: d.leads.research },
  ].filter(t => t.value > 0);
  const statusData = [
    { name: 'New', value: d.status.new },
    { name: 'Contacted', value: d.status.contacted },
    { name: 'Converted', value: d.status.converted },
    { name: 'Lost', value: d.status.lost },
  ].filter(s => s.value > 0);

  return (
    <OpsLayout>
    <div className="min-h-screen bg-slate-50" data-testid="ops-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 md:px-8 py-5">
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "'Instrument Serif', serif" }}>Dashboard</h1>
            <p className="text-xs text-slate-400 mt-0.5">Real-time overview &middot; Auto-refreshes every 60s</p>
          </div>
          <p className="text-[11px] text-slate-400 font-mono">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
        </div>
      </div>

      <div className="px-6 md:px-8 py-6 space-y-5">
        {/* ── KPI Row ── */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Link to="/ops/leads" className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md hover:-translate-y-0.5 transition-all">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400">Total Leads</p>
              <Users className="w-4 h-4 text-slate-300" />
            </div>
            <p className="font-mono text-3xl font-bold text-slate-900">{d.leads.total}</p>
            <div className="flex items-center gap-3 mt-1.5">
              <span className="flex items-center gap-1 text-[10px] font-mono"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> <span className="text-emerald-700">{d.leads.hot} hot</span></span>
              <span className="flex items-center gap-1 text-[10px] font-mono"><span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span> <span className="text-amber-700">{d.leads.warm} warm</span></span>
            </div>
          </Link>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400">Conversion</p>
              <TrendingUp className="w-4 h-4 text-emerald-400" />
            </div>
            <p className="font-mono text-3xl font-bold text-emerald-600">{convRate}%</p>
            <p className="text-[10px] text-slate-400 mt-1.5">{d.funnel.captured} captured of {d.funnel.sessions} sessions</p>
          </div>
          <Link to="/ops/drip" className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md hover:-translate-y-0.5 transition-all">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400">Emails Sent</p>
              <Mail className="w-4 h-4 text-slate-300" />
            </div>
            <p className="font-mono text-3xl font-bold text-slate-900">{d.drip.emails_sent}</p>
            <p className="text-[10px] text-slate-400 mt-1.5">{d.drip.emails_scheduled} in queue</p>
          </Link>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400">Drip Sequence</p>
              <div className={`w-2.5 h-2.5 rounded-full ${d.drip.enabled ? 'bg-emerald-500 shadow-sm shadow-emerald-500/50' : 'bg-red-400'}`} />
            </div>
            <p className={`text-lg font-bold ${d.drip.enabled ? 'text-emerald-600' : 'text-red-500'}`}>{d.drip.enabled ? 'Active' : 'Disabled'}</p>
            <p className="text-[10px] text-slate-400 mt-1.5">{d.drip.active} active &middot; {d.drip.completed} completed</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400">Delivery Rate</p>
              <Mail className="w-4 h-4 text-slate-300" />
            </div>
            <p className={`font-mono text-3xl font-bold ${(d.email_delivery?.rate || 0) >= 90 ? 'text-emerald-600' : (d.email_delivery?.rate || 0) >= 70 ? 'text-amber-600' : 'text-red-500'}`}>{d.email_delivery?.rate || 0}%</p>
            <p className="text-[10px] text-slate-400 mt-1.5">{d.email_delivery?.delivered || 0} of {d.email_delivery?.total || 0} emails</p>
          </div>
        </div>

        {/* ── Charts + Activity Row ── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
          {/* Lead Trend Chart */}
          <div className="lg:col-span-8 bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-semibold text-slate-800">Lead Activity</p>
              <div className="flex items-center gap-4">
                {[{l:'Hot',c:'#10b981'},{l:'Warm',c:'#f59e0b'},{l:'Research',c:'#cbd5e1'}].map(i => (
                  <span key={i.l} className="flex items-center gap-1.5 text-[10px] text-slate-400"><span className="w-2 h-2 rounded-full" style={{backgroundColor:i.c}}/>{i.l}</span>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={chartData} margin={{top:5,right:5,left:-20,bottom:0}}>
                <defs>
                  <linearGradient id="aHot" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10b981" stopOpacity={0.15}/><stop offset="100%" stopColor="#10b981" stopOpacity={0}/></linearGradient>
                  <linearGradient id="aWarm" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#f59e0b" stopOpacity={0.1}/><stop offset="100%" stopColor="#f59e0b" stopOpacity={0}/></linearGradient>
                  <linearGradient id="aRes" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#cbd5e1" stopOpacity={0.1}/><stop offset="100%" stopColor="#cbd5e1" stopOpacity={0}/></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{fontSize:10,fill:'#94a3b8'}} axisLine={false} tickLine={false} />
                <YAxis tick={{fontSize:10,fill:'#94a3b8'}} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{background:'#fff',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'12px',boxShadow:'0 4px 12px rgba(0,0,0,0.08)'}} />
                <Area type="monotone" dataKey="hot" stackId="1" stroke="#10b981" fill="url(#aHot)" strokeWidth={2} />
                <Area type="monotone" dataKey="warm" stackId="1" stroke="#f59e0b" fill="url(#aWarm)" strokeWidth={2} />
                <Area type="monotone" dataKey="research" stackId="1" stroke="#cbd5e1" fill="url(#aRes)" strokeWidth={1.5} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Today's Activity Feed */}
          <div className="lg:col-span-4 bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-500" />
                <p className="text-sm font-semibold text-slate-800">Today's Activity</p>
              </div>
              <span className="text-[10px] font-mono text-slate-400">{activity.length} events</span>
            </div>
            <div className="space-y-0 max-h-[240px] overflow-y-auto pr-1">
              {activity.length > 0 ? activity.map((a, i) => (
                <Link key={i} to={a.id ? `/ops/leads/${a.id}` : '#'} className="flex items-start gap-2.5 py-2 border-b border-slate-50 last:border-0 hover:bg-slate-50 -mx-2 px-2 rounded-lg transition-colors">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    a.type === 'lead_created' ? 'bg-emerald-50' :
                    a.type === 'status_changed' ? 'bg-purple-50' :
                    a.type === 'note_added' ? 'bg-blue-50' :
                    'bg-slate-50'
                  }`}>
                    {a.type === 'lead_created' ? <UserPlus className="w-3 h-3 text-emerald-600" /> :
                     a.type === 'status_changed' ? <CheckCircle className="w-3 h-3 text-purple-500" /> :
                     a.type === 'note_added' ? <MessageSquare className="w-3 h-3 text-blue-500" /> :
                     <Zap className="w-3 h-3 text-slate-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    {a.type === 'lead_created' ? (
                      <p className="text-[12px] text-slate-700"><span className="font-medium">{a.name}</span> submitted blueprint <span className={`ml-1 text-[10px] font-mono px-1.5 py-0.5 rounded-full ${a.tier === 'hot' ? 'bg-emerald-50 text-emerald-700' : a.tier === 'warm' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>{a.tier} {a.score}</span></p>
                    ) : a.type === 'status_changed' ? (
                      <p className="text-[12px] text-slate-700"><span className="font-medium">{a.name}</span> status &rarr; <span className="font-mono text-[10px] bg-slate-100 px-1 rounded">{a.new}</span></p>
                    ) : a.type === 'note_added' ? (
                      <p className="text-[12px] text-slate-700 truncate">Note on <span className="font-medium">{a.name}</span></p>
                    ) : (
                      <p className="text-[12px] text-slate-700"><span className="font-medium">{a.field}</span> edited on {a.name}</p>
                    )}
                    <p className="text-[10px] text-slate-400 font-mono mt-0.5">{a.ts ? new Date(a.ts).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : ''}{a.by ? ` · ${a.by.split('@')[0]}` : ''}</p>
                  </div>
                </Link>
              )) : (
                <div className="text-center py-10">
                  <Zap className="w-5 h-5 text-slate-200 mx-auto mb-2" />
                  <p className="text-xs text-slate-400">No activity today yet</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Bottom Row ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Conversion Funnel */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <p className="text-sm font-semibold text-slate-800 mb-4">Conversion Funnel</p>
            <div className="space-y-3">
              {[
                { label: 'Blueprint Started', value: d.funnel.sessions, pct: 100, color: 'bg-slate-900' },
                { label: 'Completed', value: d.funnel.completed, pct: d.funnel.sessions > 0 ? Math.round((d.funnel.completed/d.funnel.sessions)*100) : 0, color: 'bg-blue-500' },
                { label: 'Lead Captured', value: d.funnel.captured, pct: d.funnel.sessions > 0 ? Math.round((d.funnel.captured/d.funnel.sessions)*100) : 0, color: 'bg-emerald-500' },
              ].map((s, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px] text-slate-500">{s.label}</span>
                    <span className="font-mono text-xs text-slate-700">{s.value} <span className="text-slate-400">({s.pct}%)</span></span>
                  </div>
                  <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full ${s.color} rounded-full transition-all`} style={{width:`${s.pct}%`}} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tier Distribution + Status */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <p className="text-sm font-semibold text-slate-800 mb-3">Lead Distribution</p>
            <div className="flex items-center gap-4">
              {tierData.length > 0 ? (
                <ResponsiveContainer width={120} height={120}>
                  <PieChart>
                    <Pie data={tierData} cx="50%" cy="50%" innerRadius={35} outerRadius={55} dataKey="value" paddingAngle={3} stroke="none">
                      {tierData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              ) : <div className="w-[120px] h-[120px] bg-slate-50 rounded-full" />}
              <div className="space-y-2 flex-1">
                {[
                  { label: 'Hot', value: d.leads.hot, color: 'bg-emerald-500', textColor: 'text-emerald-700' },
                  { label: 'Warm', value: d.leads.warm, color: 'bg-amber-400', textColor: 'text-amber-700' },
                  { label: 'Research', value: d.leads.research, color: 'bg-slate-300', textColor: 'text-slate-600' },
                ].map(t => (
                  <div key={t.label} className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-[12px] text-slate-600"><span className={`w-2.5 h-2.5 rounded-sm ${t.color}`}/>{t.label}</span>
                    <span className={`font-mono text-sm font-bold ${t.textColor}`}>{t.value}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Mini status bar */}
            <div className="mt-4 pt-3 border-t border-slate-100">
              <p className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold mb-2">Lifecycle</p>
              <div className="flex gap-1 h-3 rounded-full overflow-hidden">
                {statusData.map((s, i) => (
                  <div key={i} className="h-full rounded-full" style={{ width: `${d.leads.total > 0 ? (s.value/d.leads.total)*100 : 0}%`, backgroundColor: STATUS_COLORS[i], minWidth: s.value > 0 ? '8px' : '0' }} title={`${s.name}: ${s.value}`} />
                ))}
              </div>
              <div className="flex items-center gap-3 mt-1.5">
                {statusData.map((s, i) => (
                  <span key={i} className="text-[10px] text-slate-400"><span className="inline-block w-1.5 h-1.5 rounded-full mr-1" style={{backgroundColor:STATUS_COLORS[i]}}/>{s.name} {s.value}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Leads */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-slate-800">Recent Leads</p>
              <Link to="/ops/leads" className="text-[10px] text-slate-400 hover:text-slate-700 flex items-center gap-1 transition-colors">All <ArrowRight className="w-3 h-3" /></Link>
            </div>
            <div className="space-y-1">
              {(d.recent_leads || []).slice(0, 6).map((lead, i) => {
                const tier = lead.intent?.tier;
                return (
                  <Link key={i} to={`/ops/leads/${lead.id}`} className="flex items-center justify-between py-2 group hover:bg-slate-50 -mx-2 px-2 rounded-lg transition-colors">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${tier==='hot'?'bg-emerald-500':tier==='warm'?'bg-amber-400':'bg-slate-300'}`} />
                      <div className="min-w-0">
                        <p className="text-[13px] text-slate-800 font-medium truncate group-hover:text-emerald-700 transition-colors">{lead.lead?.first_name || lead.lead?.email?.split('@')[0] || '—'}</p>
                        <p className="text-[10px] text-slate-400 font-mono truncate">{lead.lead?.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className="font-mono text-[11px] text-slate-400">{lead.intent?.score || 0}</span>
                      <Eye className="w-3.5 h-3.5 text-slate-200 group-hover:text-slate-400 transition-colors" />
                    </div>
                  </Link>
                );
              })}
              {(!d.recent_leads || d.recent_leads.length === 0) && <p className="text-xs text-slate-400 text-center py-8">No leads yet</p>}
            </div>
          </div>

          {/* MLS Listings */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-slate-800">MLS Listings</p>
              <Link to="/homes-for-sale" className="text-[10px] text-slate-400 hover:text-slate-700 flex items-center gap-1 transition-colors">View <ArrowRight className="w-3 h-3" /></Link>
            </div>
            {ddf ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Active listings</span>
                  <span className="font-mono text-lg font-bold text-emerald-700">{ddf.active}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Removed/sold</span>
                  <span className="font-mono text-sm text-slate-400">{ddf.removed}</span>
                </div>
                <div className="pt-2 border-t border-slate-100">
                  <p className="text-[10px] text-slate-400">Last synced</p>
                  <p className="text-xs font-mono text-slate-600">{ddf.last_synced ? new Date(ddf.last_synced).toLocaleString() : 'Never'}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-[10px] text-slate-400">CREA DDF connected</span>
                </div>
              </div>
            ) : (
              <p className="text-xs text-slate-400 py-4">Loading...</p>
            )}
          </div>
        </div>
      </div>
    </div>
    </OpsLayout>
  );
}
